import React, { useState, useMemo } from "react";
import { Users, ArrowRight, Plus } from "lucide-react";
import CommunityDetailScreen from "./CommunityDetail";
import { CreateCommunityModal } from "./UI";
import { useUser } from "../context/user";
import styles from "./Communities.module.css";

/**
 * Renders the user's joined communities, recommended communities feed,
 * and handles navigation to community details or community creation.
 */
export default function CommunitiesScreen({
  communities = [],
  onCreateCommunity,
  searchQuery = "", // <-- 1. Añade searchQuery aquí
}) {
  const { userData } = useUser();
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  // Default fallback communities if none are provided
  const defaultCommunities = [
    {
      id: "c1",
      name: "Beers&Beers",
      desc: "We like a good pint and a good chat. Meet with new people every Wednesday at 8 pm.",
      members: "45 members",
      image:
        "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=500&auto=format&fit=crop&q=60",
    },
    {
      id: "c2",
      name: "Run Ireland",
      desc: "A friendly running community for all experience levels meeting weekly.",
      members: "218 members",
      image:
        "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=500&auto=format&fit=crop&q=60",
    },
    {
      id: "c3",
      name: "Sharing Cult",
      desc: "If you are learning about other cultures and sharing experiences.",
      members: "92 members",
      image:
        "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=500&auto=format&fit=crop&q=60",
    },
  ];

  const rawCommunities =
    communities.length > 0 ? communities : defaultCommunities;

  // Normalize community data to ensure proper member count formatting
  const allCommunities = useMemo(() => {
    return rawCommunities.map((c) => {
      let formattedMembers = "12 members";
      if (typeof c.members === "number") {
        formattedMembers = `${c.members} members`;
      } else if (typeof c.members === "string") {
        formattedMembers = c.members.includes("member")
          ? c.members
          : `${c.members} members`;
      }
      return {
        ...c,
        members: formattedMembers,
      };
    });
  }, [rawCommunities]);

  // 2. Filtra las comunidades según el texto que escribes en la barra global
  const filteredCommunities = useMemo(() => {
    const query = (searchQuery || "").trim().toLowerCase();
    if (!query) return allCommunities;
    return allCommunities.filter(
      (c) =>
        (c?.name && c.name.toLowerCase().includes(query)) ||
        (c?.desc && c.desc.toLowerCase().includes(query))
    );
  }, [allCommunities, searchQuery]);

  // Render: Community Detail View if a community is selected
  if (selectedCommunity) {
    return (
      <CommunityDetailScreen
        community={selectedCommunity}
        onBack={() => setSelectedCommunity(null)}
      />
    );
  }

  // Render: Main Communities Screen
  return (
    <div className={styles.container}>
      {/* Top Action Header: Title & New Community Button */}
      <div className={styles.topBar}>
        <div>
          <h2 className={styles.title}>Communities</h2>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className={styles.newCommunityButton}
        >
          <Plus size={14} /> New Community
        </button>
      </div>

      {/* User's Joined Communities Section */}
      <div className={styles.yourCommunitiesSection}>
        <h3 className={styles.subtitle}>Your communities</h3>
        <div className={styles.myCommunitiesRow}>
          {allCommunities
            .filter((c) => userData?.joinedCommunities?.includes(c.id))
            .map((mc) => (
              <div
                key={mc.id}
                className={styles.smallAvatarWrapper}
                onClick={() => setSelectedCommunity(mc)}
              >
                <img
                  src={mc.image}
                  alt={mc.name}
                  className={styles.smallAvatar}
                />
                <span className={styles.smallCommunityName}>{mc.name}</span>
              </div>
            ))}
          {(!userData?.joinedCommunities ||
            userData.joinedCommunities.length === 0) && (
            <p className={styles.noInterestsText}>
              You haven't joined any communities yet.
            </p>
          )}
        </div>
      </div>

      {/* Section Header */}
      <div className={styles.sectionHeader}>
        <div className={styles.headerIconBadge}>
          <Users size={16} className={styles.brandIcon} />
        </div>
        <h2 className={styles.title}>Communities you may like</h2>
      </div>

      {/* Communities Story Feed (Usando filteredCommunities en lugar de allCommunities) */}
      <div className={styles.storyFeed}>
        {filteredCommunities.length === 0 ? (
          <p className={styles.emptyTextCenter}>No communities found.</p>
        ) : (
          filteredCommunities.map((comm) => (
            <div
              key={comm.id}
              className={styles.storyCard}
              onClick={() => setSelectedCommunity(comm)}
            >
              <img
                src={comm.image}
                alt={comm.name}
                className={styles.storyBg}
              />
              <div className={styles.storyOverlay} />
              <div className={styles.storyContent}>
                <div>
                  <h3 className={styles.storyTitle}>{comm.name}</h3>
                  <p className={styles.storyDesc}>{comm.desc}</p>
                </div>
                <div className={styles.storyFooter}>
                  <span className={styles.memberCount}>{comm.members}</span>
                  <span className={styles.exploreLink}>
                    Explore <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Community Modal */}
      <CreateCommunityModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        onCreateCommunity={(newComm) => {
          onCreateCommunity?.(newComm);
          setIsCreating(false);
        }}
      />
    </div>
  );
}
